<script setup lang="ts">
import { computed } from 'vue'
import {
  FileText,
  Paperclip,
  Monitor,
  ListTodo,
  CalendarDays,
  Loader2,
  Download,
  Check,
} from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { CopilotContextItem } from '@/types'

const props = defineProps<{
  item: CopilotContextItem
  selected: boolean
  isExtracting?: boolean
}>()

const emit = defineEmits<{
  toggle: [id: string]
  extract: [id: string]
}>()

const icon = computed(() => {
  switch (props.item.type) {
    case 'document': return FileText
    case 'attachment': return Paperclip
    case 'application': return Monitor
    case 'requirement': return ListTodo
    case 'meeting': return CalendarDays
    default: return FileText
  }
})

const hasContent = computed(() => !!props.item.content)

const isAttachment = computed(() => props.item.type === 'attachment')

const contentPreview = computed(() => {
  if (!props.item.content) return 'Sin contenido extraido'
  return props.item.content.substring(0, 200) + (props.item.content.length > 200 ? '...' : '')
})
</script>

<template>
  <div
    class="flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer group"
    @click="emit('toggle', item.id)"
  >
    <div
      class="mt-0.5 shrink-0 h-4 w-4 rounded-sm border shadow grid place-content-center transition-colors"
      :class="selected ? 'bg-primary border-primary text-primary-foreground' : 'border-primary'"
    >
      <Check v-if="selected" class="h-3 w-3" />
    </div>

    <component :is="icon" class="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />

    <div class="flex-1 min-w-0">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger as-child>
            <p class="text-sm truncate">{{ item.title }}</p>
          </TooltipTrigger>
          <TooltipContent side="right" class="max-w-[300px]">
            <p class="text-xs whitespace-pre-wrap">{{ contentPreview }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div class="flex items-center gap-1 mt-0.5">
        <Badge v-if="item.entityType" variant="secondary" class="text-[10px] px-1 py-0 h-4">
          {{ item.entityType }}
        </Badge>
        <Badge
          v-if="isAttachment"
          :variant="hasContent ? 'default' : 'outline'"
          class="text-[10px] px-1 py-0 h-4"
        >
          {{ hasContent ? 'extraido' : 'pendiente' }}
        </Badge>
      </div>
    </div>

    <Button
      v-if="isAttachment && !hasContent"
      variant="ghost"
      size="icon"
      class="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
      :disabled="isExtracting"
      @click.stop="emit('extract', item.id)"
    >
      <Loader2 v-if="isExtracting" class="h-3 w-3 animate-spin" />
      <Download v-else class="h-3 w-3" />
    </Button>
  </div>
</template>
